// The editing suite's pieces: the preview player, the filmstrip of scenes, the audio timeline (voice, music and
// effects lanes, effects draggable to nudge), and the scene detail that opens on tap (words, frame, clip with its
// model, sounds, cost). Every action calls the same endpoint the Director's tools call (src/actions.ts).
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import * as act from "../actions";
import { api } from "../api";
import { toggleAudio } from "../audio";
import { creditsFor, madeWithLine, modelLabel, sceneModels } from "../models";
import { closePlayer, playNow } from "../player";
import { isActive } from "../playback";
import { useReview } from "./ReviewTray";
import { set, useStore } from "../store";
import { clampGain, clampNudge, fmtTime, GAIN_MAX, GAIN_MIN, NUDGE_S, pxToSeconds, sceneStarts, sfxEdit, totalLength } from "../timeline";
import type { AdScene, Aspect, ModelInfo, MyModels, Project, Render, SceneSoundPreview, ShotSfx, Version } from "../types";
import { RedoSheet } from "./Artifact";
import { fmtCredits, Icon, ROLE_LABEL, Sheet, Spinner, useLoad } from "./bits";
import { FullFrame, FullFrameButton } from "./FullFrame";
import { VoicesBar } from "./Voices";
import { SceneLines } from "./SceneLines";
import { Download } from "./Download";
import { ModelOptions } from "./ModelPicker";
import { Notice } from "./Notice";
import { active, JobLine, MusicStrip, Pill, useJob } from "./ShotBoard";
import { PlaybackSurface } from "./Stage";
import { drawable, SceneBadges, SceneVisual } from "./SceneVisual";
import { isAdProject, selfDrawn, videoBrief } from "../formats";
import { OlderBadge, OlderShotNote, useOlderMarks } from "./OlderLook";
import { LibraryPick, References } from "./References"; // v4 (V4-R2)
import { Fold } from "./Fold"; // v4 (V4-P2): the scene's details fold away
import { foldSummary, setFold } from "../fold";
import { SceneCompare } from "./Compare"; // v4 (V4-MC)
import { PaidButton, usePrice, usePrices } from "./PriceFirst"; // PRICE-FIRST
import type { LipPrice } from "../price";
import { withPrice } from "../price";

const aspectClass = (p: Project) => ((p.brief.aspect ?? "9:16") as Aspect).replace(":", "x");
const pictureOf = (p: Project, s: AdScene) => p.shots[s.id]?.frame?.url || s.imageUrl || undefined;

// ------------------------------------------------------------------ preview

/**
 * The big player. It shows the selected scene (its clip looping silently, else its frame) until something plays
 * with sound; then the shared video takes the same box. One box, one size, so nothing jumps or flashes.
 */
export function Preview({ p, index, render, version }: { p: Project; index: number; render?: Render; version?: Version | null }) {
  const pb = useStore((s) => s.playback);
  const scene = p.script?.scenes[index];
  const shot = scene ? p.shots[scene.id] : undefined;
  const mine = isActive(pb) && pb.req?.surface !== "library" && pb.req?.projectId === p.id;
  const poster = scene ? pictureOf(p, scene) : (p.thumbUrl ?? undefined);
  const clipUrl = shot?.clip?.url;
  const working = useJob(p.id, scene?.id ?? null, ["frame", "clip"]);
  const renderUrl = version?.url || (render?.status === "done" ? render.videoUrl : null);
  const starts = useMemo(() => sceneStarts(p.script?.scenes ?? []), [p.script]);
  const [full, setFull] = useState(false);

  return (
    <section className={`preview preview-${aspectClass(p)}`} aria-label="Preview">
      {full && poster && <FullFrame src={poster} alt={scene?.imagePrompt ?? ""} title={`Scene ${index + 1}`} onClose={() => setFull(false)} />}
      <div className={`preview-frame frame-${aspectClass(p)}`}>
        <div className={`preview-layer${mine ? " is-hidden" : ""}`} aria-hidden={mine}>
          {clipUrl ? (
            <video key={clipUrl} src={clipUrl} poster={poster} muted loop autoPlay playsInline preload="metadata" />
          ) : poster ? (
            <img src={poster} alt={scene?.imagePrompt ?? ""} />
          ) : scene && drawable(scene.visual) ? (
            <SceneVisual scene={scene} look={videoBrief(p).look} />
          ) : (
            <span className="preview-empty">{p.script ? (working ? "Painting this scene" : "No frame yet") : "Write the script to see your scenes here"}</span>
          )}
          {working && active(working) && <span className="frame-scan" aria-hidden />}
          {poster && scene && <FullFrameButton label={`Scene ${index + 1}'s picture, full screen`} className="icon-btn preview-full" onOpen={() => setFull(true)} />}
          {scene && !drawable(scene.visual) && scene.onScreenText && <span className="preview-ost">{scene.onScreenText}</span>}
          {scene && !isAdProject(p) && <SceneBadges scene={scene} />}
          {scene && (
            <span className="preview-slate">
              <b>{index + 1}</b> {ROLE_LABEL[scene.role] ?? scene.role}
              <span className="preview-tc">
                {fmtTime(starts[index] ?? 0)} <i>/</i> {scene.durationS} s
              </span>
            </span>
          )}
          {clipUrl && (
            <button data-action="scene.play" type="button" className="preview-play" aria-label={`Play scene ${index + 1} with sound`} onClick={() => playNow({ url: clipUrl, title: `Scene ${index + 1} clip`, what: "clip", projectId: p.id, sceneId: scene?.id })}>
              <Icon name="play" size={26} />
            </button>
          )}
        </div>
        {mine && (
          <div className="preview-layer preview-live">
            <PlaybackSurface pb={pb} poster={poster} />
            <button data-action="player.close" type="button" className="icon-btn preview-close" aria-label="Stop and go back to the scene" onClick={closePlayer}>
              <Icon name="close" size={18} />
            </button>
          </div>
        )}
        <i className="corner tl" />
        <i className="corner tr" />
        <i className="corner bl" />
        <i className="corner br" />
      </div>
      <div className="preview-bar">
        {mine ? (
          <p className="preview-now">
            <span className="rec" aria-hidden /> {pb.phase === "blocked" ? "Waiting for your tap" : pb.phase === "cued" ? "Up next" : pb.phase === "ended" ? "Finished" : "Playing"}: {pb.req?.title ?? "your ad"}
          </p>
        ) : (
          <p className="preview-now muted">{scene ? `Scene ${index + 1} of ${p.script?.scenes.length}` : p.title}</p>
        )}
        {renderUrl && !mine && (
          <button data-action="versions.play" type="button" className="btn btn-primary btn-small" onClick={() => playNow({ url: renderUrl, title: version ? `${p.title}, v${version.version}` : p.title, what: "render", projectId: p.id, version: version?.version })}>
            <Icon name="play" size={14} /> Play the {isAdProject(p) ? "ad" : "video"}{version ? ` (v${version.version})` : ""}
          </button>
        )}
      </div>
      {!isAdProject(p) && p.script && !mine && <VoicesBar p={p} />}
    </section>
  );
}

// ------------------------------------------------------------------ filmstrip

export function Filmstrip({ p, selected, onSelect }: { p: Project; selected: number; onSelect: (i: number) => void }) {
  const scenes = p.script?.scenes ?? [];
  const older = useOlderMarks(p); // v4 (V4-C4): shots drawn with an older sheet
  return (
    <ol className={`strip strip-${aspectClass(p)}`} aria-label="Scenes">
      {scenes.map((s, i) => (
        <li key={s.id}>
          <StripCell p={p} s={s} i={i} on={i === selected} onSelect={() => onSelect(i)} older={older.has(s.id)} />
        </li>
      ))}
    </ol>
  );
}

function StripCell({ p, s, i, on, onSelect, older = false }: { p: Project; s: AdScene; i: number; on: boolean; onSelect: () => void; older?: boolean }) {
  const shot = p.shots[s.id];
  const job = useJob(p.id, s.id, ["frame", "clip", "sfx", "cues"]);
  const landed = useLanded(p.id, s.id);
  const pic = pictureOf(p, s);
  const draft = shot?.frame?.status === "draft" || shot?.clip?.status === "draft";
  const drawn = selfDrawn(s.visual);
  const tone = job && active(job) ? "busy" : job?.status === "failed" ? "bad" : draft ? "review" : shot?.clip?.status === "approved" ? "done" : shot?.frame?.status === "approved" || drawn ? "still" : "none";
  const word = { busy: "Working", bad: "Failed", review: "Needs your OK", done: "Clip approved", still: drawn ? "Drawn on screen" : "Frame approved", none: "Not made" }[tone];
  return (
    <button type="button" className={`strip-cell${on ? " is-on" : ""}${landed ? " is-landed" : ""}`} onClick={onSelect} aria-pressed={on} aria-label={`Scene ${i + 1}, ${ROLE_LABEL[s.role] ?? s.role}. ${word}`}>
      <span className="strip-thumb">
        {pic ? <img src={pic} alt="" loading="lazy" /> : drawable(s.visual) ? <SceneVisual scene={s} look={videoBrief(p).look} size="thumb" /> : <span className="strip-blank" />}
        {shot?.clip && (
          <span className="strip-film" aria-hidden>
            <Icon name="film" size={11} />
          </span>
        )}
        {job && active(job) && <span className="strip-progress" style={{ width: `${Math.max(6, Math.round(job.progress * 100))}%` }} />}
        {older && <OlderBadge count={1} label="older" className="strip-older" />}
      </span>
      <span className="strip-meta">
        <b>{i + 1}</b>
        <span className="strip-role">{ROLE_LABEL[s.role] ?? s.role}</span>
        <span className={`strip-dot strip-dot-${tone}`} title={word} />
      </span>
    </button>
  );
}

/** True for a few seconds after a scene's frame or clip lands (the filmstrip's satisfying beat). */
function useLanded(projectId: string, sceneId: string) {
  const at = useStore((s) => {
    let t = 0;
    for (const j of Object.values(s.jobs)) if (j.projectId === projectId && j.sceneId === sceneId && j.status === "done" && (j.kind === "clip" || j.kind === "frame")) t = Math.max(t, Date.parse(j.updatedAt) || 0);
    return t;
  });
  const [on, setOn] = useState(false);
  const first = useRef(at);
  useEffect(() => {
    if (!at || at === first.current || Date.now() - at > 5000) return;
    setOn(true);
    const t = window.setTimeout(() => setOn(false), 1400);
    return () => window.clearTimeout(t);
  }, [at]);
  return on;
}

// ------------------------------------------------------------------ audio timeline

/** Fixed bars seeded by text, so each block looks like its own audio without decoding anything. */
function Wave({ seed, bars = 40, className = "" }: { seed: string; bars?: number; className?: string }) {
  let h = 7;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const vals = Array.from({ length: bars }, (_, i) => {
    h = (h * 1664525 + 1013904223) >>> 0;
    const env = 0.35 + 0.65 * Math.abs(Math.sin((i / bars) * Math.PI * 1.6 + (h % 7)));
    return Math.max(0.12, (((h >>> 8) % 100) / 100) * env);
  });
  return (
    <svg className={`wave ${className}`} viewBox={`0 0 ${bars * 3} 20`} preserveAspectRatio="none" aria-hidden>
      {vals.map((v, i) => (
        <rect key={i} x={i * 3} y={10 - v * 9} width="1.6" height={v * 18} rx="0.8" />
      ))}
    </svg>
  );
}

const PX_PER_S = 34;

export function Timeline({ p, selected, onSelect, onProject }: { p: Project; selected: number; onSelect: (i: number) => void; onProject: (p: Project) => void }) {
  const scenes = p.script?.scenes ?? [];
  const total = totalLength(scenes) || 1;
  const starts = sceneStarts(scenes);
  const musicJob = useJob(p.id, null, ["music"]);
  const [fx, setFx] = useState<{ sceneId: string; index: number } | null>(null);
  const [music, setMusic] = useState(false);
  const pct = (s: number) => `${(s / total) * 100}%`;
  const ticks = Array.from({ length: Math.floor(total) + 1 }, (_, i) => i);
  const lane = useRef<HTMLDivElement>(null);

  return (
    <section className="tl" aria-label="Audio timeline">
      <div className="tl-labels" aria-hidden>
        <span className="tl-label tl-label-ruler" />
        <span className="tl-label">Voice</span>
        <span className="tl-label">Music</span>
        <span className="tl-label">Effects</span>
      </div>
      <div className="tl-scroll">
        <div className="tl-inner" style={{ minWidth: `${total * PX_PER_S}px` }} ref={lane}>
          {/* the selected scene, across every lane */}
          <span className="tl-sel" style={{ left: pct(starts[selected] ?? 0), width: pct(scenes[selected]?.durationS ?? 0) }} aria-hidden />
          <div className="tl-ruler" aria-hidden>
            {ticks.map((t) => (
              <span key={t} className={`tl-tick${t % 5 === 0 ? " is-major" : ""}`} style={{ left: pct(t) }}>
                {t % 5 === 0 && <i>{fmtTime(t).replace(/\.0$/, "")}</i>}
              </span>
            ))}
          </div>

          <div className="tl-lane tl-voice">
            {scenes.map((s, i) => (
              <button type="button" key={s.id} className={`tl-block${i === selected ? " is-on" : ""}`} style={{ left: pct(starts[i]), width: pct(s.durationS) }} onClick={() => onSelect(i)} aria-label={`Scene ${i + 1} voiceover: ${s.voiceover}`}>
                <Wave seed={s.voiceover} bars={Math.max(8, Math.round(s.durationS * 5))} />
              </button>
            ))}
          </div>

          <div className="tl-lane tl-music">
            {p.music ? (
              <button type="button" className={`tl-block tl-bed${p.music.status === "draft" ? " is-draft" : ""}`} style={{ left: 0, width: "100%" }} onClick={() => setMusic(true)} aria-label={`Music bed, ${p.music.status === "draft" ? "waiting for your OK" : "approved"}`}>
                <Wave seed={p.music.prompt || "music"} bars={Math.round(total * 4)} />
                {p.music.status === "draft" && <span className="tl-flag">Needs your OK</span>}
              </button>
            ) : musicJob && active(musicJob) ? (
              <span className="tl-block tl-bed is-working" style={{ left: 0, width: `${Math.max(4, musicJob.progress * 100)}%` }}>
                <span className="tl-flag">Composing {Math.round(musicJob.progress * 100)}%</span>
              </span>
            ) : (
              <button type="button" className="tl-empty" onClick={() => setMusic(true)}>
                <Icon name="plus" size={14} /> Add music
              </button>
            )}
          </div>

          <div className="tl-lane tl-fx">
            {scenes.map((s, i) => {
              const shot = p.shots[s.id];
              return (
                <span key={s.id} className="tl-scene" style={{ left: pct(starts[i]), width: pct(s.durationS) }}>
                  {shot?.ambience && <span className="tl-amb" title="Ambience" />}
                  {(shot?.sfx ?? []).map((e, k) => (
                    <SfxMarker key={e.assetId || k} p={p} scene={s} index={k} fx={e} lane={lane} total={total} onProject={onProject} onOpen={() => setFx({ sceneId: s.id, index: k })} />
                  ))}
                </span>
              );
            })}
          </div>
          {scenes.map((s, i) => i > 0 && <span key={s.id} className="tl-cut" style={{ left: pct(starts[i]) }} aria-hidden />)}
        </div>
      </div>
      <p className="tl-hint">Drag an effect to nudge it. Tap it to mute or change its level.</p>
      {fx && <SfxSheet p={p} sceneId={fx.sceneId} index={fx.index} onClose={() => setFx(null)} onProject={onProject} />}
      {music && <MusicSheet p={p} onClose={() => setMusic(false)} />}
    </section>
  );
}

function SfxMarker({ p, scene, index, fx, lane, total, onProject, onOpen }: { p: Project; scene: AdScene; index: number; fx: ShotSfx; lane: React.RefObject<HTMLDivElement | null>; total: number; onProject: (p: Project) => void; onOpen: () => void }) {
  const [at, setAt] = useState<number | null>(null);
  const drag = useRef<{ x: number; start: number; moved: boolean } | null>(null);
  const shown = at ?? fx.atS;
  const save = async (atS: number) => {
    if (atS === fx.atS) return setAt(null);
    const next = await act.editSfx(p.id, scene.id, sfxEdit(index, fx, { atS }), `moved "${fx.label}" to ${atS.toFixed(2)} s`);
    if (next) onProject(next);
    setAt(null);
  };
  const down = (e: PointerEvent<HTMLButtonElement>) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, start: fx.atS, moved: false };
  };
  const move = (e: PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) > 4) d.moved = true;
    if (!d.moved) return;
    const w = lane.current?.getBoundingClientRect().width ?? 0;
    setAt(clampNudge(fx.detectedAtS, d.start + pxToSeconds(dx, w, total), scene.durationS));
  };
  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) return onOpen();
    if (at !== null) void save(at);
  };
  const key = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    void save(clampNudge(fx.detectedAtS, fx.atS + (e.key === "ArrowLeft" ? -0.1 : 0.1), scene.durationS));
  };
  const lo = Math.max(0, fx.detectedAtS - NUDGE_S);
  const hi = Math.min(scene.durationS, fx.detectedAtS + NUDGE_S);
  return (
    <>
      {at !== null && <span className="fx-range" style={{ left: `${(lo / scene.durationS) * 100}%`, width: `${((hi - lo) / scene.durationS) * 100}%` }} aria-hidden />}
      <button
        type="button"
        className={`fx${fx.enabled ? "" : " is-off"}${at !== null ? " is-drag" : ""}`}
        style={{ left: `${(shown / scene.durationS) * 100}%` }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={() => ((drag.current = null), setAt(null))}
        onKeyDown={key}
        aria-label={`${fx.label} at ${shown.toFixed(1)} seconds${fx.enabled ? "" : ", muted"}. Arrow keys nudge it; Enter opens it.`}
      >
        <span className="fx-pin" aria-hidden />
        <span className="fx-label">{fx.label}</span>
      </button>
    </>
  );
}

function SfxSheet({ p, sceneId, index, onClose, onProject }: { p: Project; sceneId: string; index: number; onClose: () => void; onProject: (p: Project) => void }) {
  const scene = p.script?.scenes.find((s) => s.id === sceneId);
  const fx = p.shots[sceneId]?.sfx[index];
  const [gain, setGain] = useState(fx?.gainDb ?? 0);
  const playing = useStore((s) => s.playing === `fx-${fx?.assetId}`);
  if (!scene || !fx) return null;
  const n = (p.script?.scenes.indexOf(scene) ?? 0) + 1;
  const commit = async (patch: Partial<Pick<ShotSfx, "atS" | "gainDb" | "enabled">>, said: string) => {
    const next = await act.editSfx(p.id, sceneId, sfxEdit(index, fx, patch), said);
    if (next) onProject(next);
  };
  return (
    <Sheet title={fx.label} onClose={onClose}>
      <div className="sheet-body fx-sheet">
        <p className="muted">
          Scene {n}, at {fmtTime(fx.atS)}
          {Math.abs(fx.atS - fx.detectedAtS) > 0.01 ? ` (detected at ${fmtTime(fx.detectedAtS)})` : ""}
        </p>
        <div className="fx-row">
          <button type="button" className={`card-audio-btn${playing ? " is-on" : ""}`} onClick={() => toggleAudio(`fx-${fx.assetId}`, fx.url)} aria-label={playing ? "Stop" : `Play ${fx.label}`}>
            <Icon name={playing ? "pause" : "play"} size={18} />
          </button>
          <button data-action="scene.sound-edit" type="button" className={`btn btn-quiet btn-small${fx.enabled ? "" : " is-on"}`} aria-pressed={!fx.enabled} onClick={() => void commit({ enabled: !fx.enabled }, fx.enabled ? `muted "${fx.label}"` : `unmuted "${fx.label}"`)}>
            <Icon name={fx.enabled ? "mute" : "volume"} size={16} /> {fx.enabled ? "Mute it" : "Unmute it"}
          </button>
        </div>
        <label className="field">
          <span className="field-label">
            Level <b className="fx-db">{gain > 0 ? "+" : ""}{gain.toFixed(1)} dB</b>
          </span>
          <input
            type="range"
            min={GAIN_MIN}
            max={GAIN_MAX}
            step={0.5}
            value={gain}
            onChange={(e) => setGain(clampGain(+e.target.value))}
            onPointerUp={() => gain !== fx.gainDb && void commit({ gainDb: gain }, `set "${fx.label}" to ${gain} dB`)}
            onKeyUp={() => gain !== fx.gainDb && void commit({ gainDb: gain }, `set "${fx.label}" to ${gain} dB`)}
            aria-valuetext={`${gain} decibels`}
          />
          <span className="field-hint">Quieter to the left, louder to the right. The mix keeps the voice on top.</span>
        </label>
        <div className="fx-row">
          <span className="field-label">Timing</span>
          <button data-action="scene.sound-edit" type="button" className="icon-btn" aria-label="Earlier by a tenth of a second" onClick={() => void commit({ atS: clampNudge(fx.detectedAtS, fx.atS - 0.1, scene.durationS) }, `nudged "${fx.label}" earlier`)}>
            <Icon name="back" size={16} />
          </button>
          <span className="fx-time">{fmtTime(fx.atS)}</span>
          <button data-action="scene.sound-edit" type="button" className="icon-btn fx-later" aria-label="Later by a tenth of a second" onClick={() => void commit({ atS: clampNudge(fx.detectedAtS, fx.atS + 0.1, scene.durationS) }, `nudged "${fx.label}" later`)}>
            <Icon name="back" size={16} />
          </button>
          {Math.abs(fx.atS - fx.detectedAtS) > 0.01 && (
            <button data-action="scene.sound-edit" type="button" className="btn btn-quiet btn-small" onClick={() => void commit({ atS: fx.detectedAtS }, `put "${fx.label}" back where it was detected`)}>
              Reset
            </button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function MusicSheet({ p, onClose }: { p: Project; onClose: () => void }) {
  return (
    <Sheet title="Music bed" onClose={onClose}>
      <div className="sheet-body">
        <MusicStrip p={p} />
      </div>
    </Sheet>
  );
}

// ------------------------------------------------------------------ scene detail

/** "Preview with sound": the scene's clip (or still) with its sounds and any recorded voice lines mixed over it, played
 *  inline. Free (the daily preview allowance); remounted (cleared) when the sounds or the picture change. */
function SoundPreview({ p, sceneId, index }: { p: Project; sceneId: string; index: number }) {
  const [working, setWorking] = useState(false);
  const [pv, setPv] = useState<SceneSoundPreview | null>(null);
  const go = async () => {
    setWorking(true);
    const r = await act.previewSceneSound(p.id, sceneId);
    setWorking(false);
    if (r?.url) setPv(r);
  };
  return (
    <div className="detail-sound-preview">
      <button data-action="scene.sound-preview" type="button" className="btn btn-quiet btn-small" disabled={working} onClick={() => void go()} aria-label={`Preview scene ${index + 1} with its sounds`}>
        {working ? <Spinner /> : <Icon name="play" size={14} />} {working ? "Mixing the sounds…" : pv ? "Preview again" : "Preview with sound"}
      </button>
      {!pv && !working && <span className="field-hint"> Free, a few seconds. Nothing is saved.</span>}
      {pv?.url && (
        <video className="detail-sound-preview-video" src={pv.url} controls autoPlay playsInline aria-label={`Scene ${index + 1} with ${pv.sounds.length ? pv.sounds.join(", ") : "its ambience"}`} />
      )}
    </div>
  );
}

export function SceneDetail({ p, index, onEditWords, models, myModels }: { p: Project; index: number; onEditWords: (s: AdScene) => void; models: ModelInfo[] | null; myModels: MyModels | null }) {
  const scene = p.script?.scenes[index];
  const frameJob = useJob(p.id, scene?.id ?? null, ["frame"]);
  const clipJob = useJob(p.id, scene?.id ?? null, ["clip"]);
  const sfxJob = useJob(p.id, scene?.id ?? null, ["sfx", "cues"]);
  const pick = useStore((s) => (scene ? s.sceneModel[`${p.id}/${scene.id}`] : undefined));
  const review = useReview(useMemo(() => [p], [p]));
  const failure = review.find((r) => r.kind === "failed" && r.sceneId === scene?.id);
  const [redo, setRedo] = useState<"frame" | "clip" | null>(null);
  const [animate, setAnimate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fullFrame, setFullFrame] = useState(false);
  const olderMarks = useOlderMarks(p); // v4 (V4-C4)
  // PRICE-FIRST: each step's own price on its button (frame, sounds), from the same functions the steps charge with
  const { prices } = usePrices(p.id, scene?.id ?? null, [JSON.stringify(p.shots[scene?.id ?? ""] ?? null), JSON.stringify(p.models ?? null), JSON.stringify(p.modelPrefs ?? null), p.brief.durationS]);
  const { data: est } = useLoad(() => (scene ? api.estimate(p.id, { scope: "scene", sceneId: scene.id, through: "sfx" }) : Promise.resolve(null)), [p.id, scene?.id, JSON.stringify(p.shots[scene?.id ?? ""] ?? null)]);
  if (!scene) return null;
  const shot = p.shots[scene.id] ?? { refAssetIds: [], sfx: [] };
  const f = shot.frame;
  const c = shot.clip;
  const catalog = models ?? [];
  // Keaton 2026-10-30: the scene's picture and video models are separate picks, each "Video default (X)" when unset
  const picks = sceneModels(shot, { project: p.modelPrefs, account: myModels?.account, defaults: { ...myModels?.defaults, ...p.models } }, catalog);
  const modelId = pick ?? picks.clip.id;
  const madeWith = madeWithLine(shot, catalog);
  const pickModel = (step: "image" | "clip", m: ModelInfo | null) => {
    const own = step === "clip" ? picks.clip.own : picks.image.own;
    if ((m?.id ?? null) === own) return;
    const fallback = step === "clip" ? picks.clip.defaultId : picks.image.defaultId;
    if (step === "clip") set((st) => { const next = { ...st.sceneModel }; delete next[`${p.id}/${scene!.id}`]; return { sceneModel: next }; });
    setFold(step === "clip" ? "scene.model" : "scene.image-model", false);
    void act.setSceneModel(p.id, scene!.id, step, m?.id ?? null, m?.label ?? modelLabel(fallback, catalog));
  };
  const model = catalog.find((m) => m.id === modelId);
  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="detail">
      <header className="detail-head">
        <p className="detail-kicker">
          {ROLE_LABEL[scene.role] ?? scene.role} <span>{scene.durationS} s</span>
        </p>
        {isAdProject(p) ? (
          scene.voiceover && <p className="detail-vo">“{scene.voiceover}”</p>
        ) : (
          <SceneLines p={p} index={index} name={(id) => castName(p, id)} />
        )}
        {scene.onScreenText && <p className="detail-ost">On screen: {scene.onScreenText}</p>}
        <button type="button" className="btn btn-quiet btn-small" onClick={() => onEditWords(scene)}>
          <Icon name="edit" size={14} /> Edit the words
        </button>
        {!isAdProject(p) && (scene.voiceover || !!scene.lines?.length) && <VoicesBar p={p} sceneId={scene.id} index={index} />}
      </header>

      {olderMarks.get(scene.id) && <OlderShotNote mark={olderMarks.get(scene.id)!} projectId={p.id} sceneId={scene.id} sceneIndex={index + 1} />}

      {failure && (
        <div className="detail-fail" role="alert">
          <Icon name="alert" size={18} />
          <div>
            <p>{failure.note}</p>
            {failure.suggestion && failure.failedKind === "clip" && (
              <PaidButton
                price={{ step: "clip", credits: failure.suggestion.credits, clips: 1, needsYes: true, model: failure.suggestion.model }}
                label={`Try ${failure.suggestion.label}`}
                className="btn btn-primary btn-small"
                dataAction="scene.try-model"
                disabled={busy}
                ask={{ title: `Try ${failure.suggestion.label} on scene ${index + 1}?`, lede: `A new clip of this scene on ${failure.suggestion.label}.`, go: `Try ${failure.suggestion.label}` }}
                run={(credits) => act.startStep(p.id, "clip", { sceneId: scene.id, model: failure.suggestion!.model }, credits)}
              />
            )}
          </div>
        </div>
      )}

      {selfDrawn(scene.visual) ? (
        <div className="detail-step detail-drawn">
          <div className="detail-step-head">
            <span className="step-name">
              <Icon name="sparkle" size={16} /> Drawn on screen
            </span>
            <Pill tone="done">No picture needed</Pill>
          </div>
          <div className="detail-drawn-card">
            <SceneVisual scene={scene} look={videoBrief(p).look} />
          </div>
          <p className="detail-prompt">{scene.visual?.kind === "math" ? "The equations build step by step in the video, set properly, in time with the narration." : "The words animate in time with the voice. Nothing to make or approve here."}</p>
        </div>
      ) : (
      <>
      <div className="detail-step">
        <div className="detail-step-head">
          <span className="step-name">
            <Icon name="image" size={16} /> Frame
          </span>
          {active(frameJob) ? <Pill tone="busy">Painting</Pill> : f ? <Pill tone={f.status === "approved" ? "done" : "draft"}>{f.status === "approved" ? "Approved" : "Needs your OK"}</Pill> : <Pill tone="off">Not made</Pill>}
          <span className="step-acts">
            {!active(frameJob) && !f && (
              <PaidButton
                price={prices?.prices.frame}
                label="Make it"
                dataAction="scene.frame"
                disabled={busy}
                ask={{ title: `Make scene ${index + 1}'s frame?`, lede: "A still picture for this scene, to approve before it's animated.", go: "Make it" }}
                run={(credits) => act.startStep(p.id, "frame", { sceneId: scene.id }, credits)}
              />
            )}
            {!active(frameJob) && f?.status === "draft" && (
              <button data-action="scene.approve" type="button" className="btn btn-primary btn-small" disabled={busy} onClick={run(() => act.approve(p.id, scene.id, "frame"))}>
                Approve
              </button>
            )}
            {!active(frameJob) && f && <Download url={f.downloadUrl} label={`scene ${index + 1} frame`} />}
            {f?.url && <FullFrameButton label={`Scene ${index + 1}'s frame, full screen`} onOpen={() => setFullFrame(true)} />}
            {fullFrame && f?.url && <FullFrame src={f.url} alt={scene.imagePrompt ?? ""} title={`Scene ${index + 1} frame`} onClose={() => setFullFrame(false)} />}
            {!active(frameJob) && f && (
              <button data-action="scene.redo" type="button" className="icon-btn" aria-label="Redo the frame" title="Redo" disabled={busy} onClick={() => setRedo("frame")}>
                <Icon name="redo" size={18} />
              </button>
            )}
          </span>
        </div>
        {frameJob && <JobLine job={frameJob} />}
        {madeWith && <p className="detail-made muted" data-testid="scene-made-with">{madeWith}</p>}
        {/* Keaton 2026-10-30: the scene's own picture model (its video model is a separate pick, below) */}
        <Fold id="scene.image-model" title="Picture model" summary={picks.image.label} className="detail-fold">
          <p className="detail-model-now">
            <span className="field-label">Picture model{f ? " for the next picture" : ""}</span>
            <strong>{picks.image.label}</strong>
          </p>
          {picks.image.own && (
            <button type="button" className="btn btn-quiet btn-small" data-action="scene.image-model" disabled={busy} onClick={() => pickModel("image", null)}>
              Use the video default ({modelLabel(picks.image.defaultId, catalog)})
            </button>
          )}
          {models ? <ModelOptions dataAction="scene.image-model" step="image" models={models} value={picks.image.id} onPick={(m) => pickModel("image", m)} /> : <Spinner label="Loading the models" />}
        </Fold>
        {/* v4 (V4-P2): the prompt behind the picture, one line until opened */}
        <Fold id="scene.prompt" title="Picture description" summary={f?.prompt || scene.imagePrompt} className="detail-fold">
          <p className="detail-prompt">{f?.prompt || scene.imagePrompt}</p>
        </Fold>
      </div>
      <References p={p} scene={scene} index={index} models={catalog} frameBusy={!!active(frameJob)} />

      <div className={`detail-step${!f ? " is-later" : ""}`}>
        <div className="detail-step-head">
          <span className="step-name">
            <Icon name="film" size={16} /> Clip
          </span>
          {active(clipJob) ? <Pill tone="busy">Animating</Pill> : c ? <Pill tone={c.status === "approved" ? "done" : "draft"}>{c.status === "approved" ? "Approved" : "Needs your OK"}</Pill> : <Pill tone="off">{f?.status === "approved" ? "Still image for now" : "After the frame"}</Pill>}
          <span className="step-acts">
            {!active(clipJob) && !c && f?.status === "approved" && (
              <button type="button" className="btn btn-quiet btn-small" disabled={busy} onClick={() => setAnimate(true)}>
                {withPrice("Animate", model ? creditsFor(model, scene.durationS) : null)}
              </button>
            )}
            {!active(clipJob) && c?.status === "draft" && (
              <button data-action="scene.approve" type="button" className="btn btn-primary btn-small" disabled={busy} onClick={run(() => act.approve(p.id, scene.id, "clip"))}>
                Approve
              </button>
            )}
            {!active(clipJob) && c && <Download url={c.downloadUrl} label={`scene ${index + 1} clip`} />}
            {!active(clipJob) && c && index + 1 < (p.script?.scenes.length ?? 0) && (
              <button data-action="scene.last-frame" type="button" className="btn btn-quiet btn-small" disabled={busy} title={`Scene ${index + 2}'s next frame starts from where this clip ends. Free.`} onClick={run(() => act.useLastFrame(p.id, scene.id))}>
                Use last frame for next scene
              </button>
            )}
            {!active(clipJob) && c && (
              <button data-action="scene.redo" type="button" className="icon-btn" aria-label="Redo the clip" title="Redo" disabled={busy} onClick={() => setRedo("clip")}>
                <Icon name="redo" size={18} />
              </button>
            )}
          </span>
        </div>
        <ClipEndRow p={p} sceneId={scene.id} index={index} busy={busy} />
        {c && <ClipFitRow p={p} sceneId={scene.id} index={index} busy={busy} />}
        {/* v4 (V4-P2): how it moves and which model makes the next take; the take's price stays on the row */}
        <Fold id="scene.model" title="Motion and model" summary={pick ? modelLabel(pick, catalog) : picks.clip.label} price={model ? creditsFor(model, scene.durationS) : null} className="detail-fold">
          {c?.motionPrompt && <p className="detail-prompt">Motion: {c.motionPrompt}</p>}
          {prices?.prices.clip?.lipSync && <LipSyncRow p={p} sceneId={scene.id} lip={prices.prices.clip.lipSync} hasClip={!!c} busy={busy} />}
          <p className="detail-model-now">
            <span className="field-label">Video model{c ? " for the next take" : ""}</span>
            <strong>{pick ? modelLabel(pick, catalog) : picks.clip.label}</strong>
            {c?.model && c.model !== modelId && <span className="muted"> This take: {modelLabel(c.model, catalog)}</span>}
          </p>
          {picks.clip.own && (
            <button type="button" className="btn btn-quiet btn-small" data-action="scene.model" disabled={busy} onClick={() => pickModel("clip", null)}>
              Use the video default ({modelLabel(picks.clip.defaultId, catalog)})
            </button>
          )}
          {models ? (
            <ModelOptions
              dataAction="scene.model"
              step="clip"
              models={models}
              value={modelId}
              durationS={scene.durationS}
              {...(prices?.prices.clip?.lipSync ? { lipSyncIds: prices.prices.clip.lipSync.suggest.map((s) => s.id) } : {})}
              onPick={(m) => pickModel("clip", m)}
            />
          ) : (
            <Spinner label="Loading the models" />
          )}
        </Fold>
        {clipJob && !(failure && clipJob.status === "failed") && <JobLine job={clipJob} />}
      </div>
      {/* v4 (V4-MC): try this scene on other models, side by side */}
      <SceneCompare p={p} sceneId={scene.id} index={index} />

      <div className={`detail-step${!c ? " is-later" : ""}`}>
        <div className="detail-step-head">
          <span className="step-name">
            <Icon name="wave" size={16} /> Sounds
          </span>
          {active(sfxJob) ? <Pill tone="busy">Listening</Pill> : shot.sfx.length ? <Pill tone="done">{`${shot.sfx.filter((s) => s.enabled).length} of ${shot.sfx.length} on`}</Pill> : <Pill tone="off">{c ? "None yet" : "After the clip"}</Pill>}
          <span className="step-acts">
            {!active(sfxJob) && c && (
              <PaidButton
                price={prices?.prices.sfx}
                label="Add sounds"
                dataAction="scene.sounds"
                className={shot.sfx.length ? "icon-btn" : "btn btn-quiet btn-small"}
                {...(shot.sfx.length ? { icon: "redo", iconOnly: true, ariaLabel: "Redo the sounds" } : {})}
                disabled={busy}
                ask={{ title: `${shot.sfx.length ? "Redo" : "Add"} scene ${index + 1}'s sounds?`, lede: "Effects placed on the clip and a bed under it. What isn't used comes back.", go: shot.sfx.length ? "Redo them" : "Add them" }}
                run={(credits) => act.startStep(p.id, "sfx", { sceneId: scene.id }, credits)}
              />
            )}
          </span>
        </div>
        {sfxJob && <JobLine job={sfxJob} />}
        {/* Keaton 2026-10-30: hear the sounds over the clip (or the still) before keeping them; free, made at once */}
        {!active(sfxJob) && (shot.sfx.some((x) => x.enabled) || !!shot.ambience) && (c || f) && <SoundPreview key={JSON.stringify([shot.sfx, shot.ambience?.assetId, c?.assetId ?? f?.assetId])} p={p} sceneId={scene.id} index={index} />}
        {/* v4 (V4-P2): each sound with its timing and level, folded under the step */}
        {!!shot.sfx.length && (
          <Fold id="scene.sounds" title="Sound list" count={shot.sfx.length + (shot.ambience ? 1 : 0)} summary={foldSummary([...shot.sfx.map((e) => e.label), shot.ambience && "ambience"])} className="detail-fold">
            <ul className="detail-sfx">
              {shot.sfx.map((e) => (
                <li key={e.assetId} className={e.enabled ? "" : "is-off"}>
                  <button type="button" className="icon-btn" aria-label={`Play ${e.label}`} onClick={() => toggleAudio(`sfx-${e.assetId}`, e.url)}>
                    <Icon name="play" size={12} />
                  </button>
                  <span className="detail-sfx-name">{e.label}</span>
                  <span className="detail-sfx-meta">
                    {fmtTime(e.atS)}, {e.gainDb > 0 ? "+" : ""}
                    {e.gainDb} dB{e.enabled ? "" : ", muted"}
                  </span>
                  <Download url={e.downloadUrl} label={e.label} />
                </li>
              ))}
              {shot.ambience && (
                <li>
                  <button type="button" className="icon-btn" aria-label="Play the ambience" onClick={() => toggleAudio(`amb-${scene.id}`, shot.ambience?.url)}>
                    <Icon name="play" size={12} />
                  </button>
                  <span className="detail-sfx-name">Ambience</span>
                  <span className="detail-sfx-meta">under the whole scene</span>
                  <Download url={shot.ambience.downloadUrl} label={`scene ${index + 1} ambience`} />
                </li>
              )}
            </ul>
          </Fold>
        )}
      </div>

      </>
      )}

      <p className="detail-cost">
        <span>Left to make for this scene</span>
        <b>{est ? `${fmtCredits(est.credits)} credits` : "…"}</b>
      </p>

      {redo && (
        <RedoSheet
          a={{ kind: redo, projectId: p.id, sceneId: scene.id, sceneIndex: index + 1, title: `Scene ${index + 1} ${redo}` }}
          credits={redo === "clip" ? (model ? creditsFor(model, scene.durationS) : null) : prices ? (prices.prices.frame?.credits ?? null) : undefined}
          onClose={() => setRedo(null)}
          onRedo={async (note, expectCredits) => {
            setBusy(true);
            try {
              // cost audit + PRICE-FIRST: charged at most the price on the sheet's button; a dearer one asks again
              return await act.startStep(p.id, redo, { sceneId: scene.id, redo: true, note, ...(redo === "clip" && pick ? { model: pick } : {}) }, expectCredits);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
      {animate && <AnimateSheet p={p} scene={scene} index={index} models={catalog} modelId={modelId} onClose={() => setAnimate(false)} />}
    </div>
  );
}

/** Video always needs a yes: the model and its price, then go. */
/** lip sync (2026-10-30): a talking scene's switch, what it adds to the clip, the warning, and thumbs on its clip. */
function LipSyncRow({ p, sceneId, lip, hasClip, busy }: { p: Project; sceneId: string; lip: LipPrice; hasClip: boolean; busy: boolean }) {
  const [rated, setRated] = useState<"good" | "bad" | null>(null);
  const adds = lip.takes + lip.pass;
  return (
    <div className="detail-lipsync">
      <label className="field-inline">
        <input type="checkbox" data-action="scene.lip-sync" checked={lip.on} disabled={busy} onChange={(e) => void act.setLipSync(p.id, sceneId, e.target.checked)} />
        <span>Lip sync</span>
        <span className="muted">{lip.mode === "audio" ? " the model takes the characters' voices" : lip.mode === "pass" ? " a lip-sync pass after the clip" : lip.on ? "" : " off: the words go in the prompt only"}{adds ? ` (+${adds} credits in the clip's price)` : ""}</span>
      </label>
      {lip.warn && <Notice tone="warning" text={`${lip.warn}${lip.suggest.length ? ` Good for lip sync: ${lip.suggest.slice(0, 2).map((s) => s.short).join(", ")}.` : ""}`} />}
      {lip.on && <LipLead p={p} sceneId={sceneId} busy={busy} />}
      {hasClip && (
        <span className="lipsync-rate">
          <span className="muted">How did the lip sync look?</span>
          {(["good", "bad"] as const).map((v) => (
            <button key={v} type="button" data-action="scene.lip-rate" className={`icon-btn${rated === v ? " is-on" : ""}`} aria-label={v === "good" ? "Lip sync looked good" : "Lip sync didn't match"} title={v === "good" ? "Good" : "Didn't match"} disabled={!!rated} onClick={async () => (await act.rateLipSync(p.id, sceneId, v)) && setRated(v)}>
              <Icon name={v === "good" ? "check" : "close"} size={16} />
            </button>
          ))}
        </span>
      )}
    </div>
  );
}

/** 2026-10-31: silence before the character speaks (after the narrator's line), in the clip's audio and the render. */
function LipLead({ p, sceneId, busy }: { p: Project; sceneId: string; busy: boolean }) {
  const saved = p.shots[sceneId]?.lipLeadS ?? 0;
  const [v, setV] = useState(String(saved));
  const save = () => {
    const n = Math.max(0, Math.min(6, Number(v) || 0));
    setV(String(n));
    if (n !== saved) void act.setLipLead(p.id, sceneId, n);
  };
  return (
    <label className="lip-lead field-inline" title="Silence before the character starts speaking in the clip, so their words come after the narrator's">
      <span className="muted">Speech starts after</span>
      <input data-action="scene.lip-lead" type="number" min={0} max={6} step={0.25} inputMode="decimal" value={v} disabled={busy} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && save()} aria-label="Lip-sync lead-in in seconds" />
      <span className="muted">s of silence</span>
    </label>
  );
}

function AnimateSheet({ p, scene, index, models, modelId, onClose }: { p: Project; scene: AdScene; index: number; models: ModelInfo[]; modelId: string | null; onClose: () => void }) {
  const [model, setModel] = useState<string | null>(modelId);
  const [motion, setMotion] = useState("");
  const [going, setGoing] = useState(false);
  // fix-produce: a refused yes keeps the sheet open: the new price to ask about again, or what went wrong
  const [repriced, setRepriced] = useState<{ model: string | null; credits: number; text: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const m = models.find((x) => x.id === model);
  // lip sync: the server's price for this model includes a talking scene's line reads and pass; until it's in, the catalog's
  const server = usePrice(p.id, "clip", scene.id, [JSON.stringify(p.shots[scene.id] ?? null)], model);
  const lip = server?.model === model ? server.lipSync : undefined;
  const quoted = server && server.model === model && server.credits !== null ? server.credits : m ? creditsFor(m, scene.durationS) : null;
  const credits = repriced && repriced.model === model ? repriced.credits : quoted;
  return (
    <Sheet title={`Animate scene ${index + 1}?`} onClose={onClose}>
      <form
        className="sheet-body confirm"
        onSubmit={async (e) => {
          e.preventDefault();
          setGoing(true);
          setFailure(null);
          const r = await act.makeClipConfirmed(p.id, scene.id, model ?? undefined, motion.trim() || undefined, credits ?? undefined);
          setGoing(false);
          if (r.kind === "ok") return onClose();
          if (r.kind === "reprice") setRepriced({ model, credits: r.price.credits, text: r.text });
          else setFailure(r.text);
        }}
      >
        {repriced && repriced.model === model && <Notice tone="warning" text={`${repriced.text} Go ahead?`} code="CONFIRM_REQUIRED" />}
        {failure && <Notice tone="error" text={`Couldn't start the clip. ${failure}`} />}
        <p className="confirm-num">
          {credits == null ? "…" : fmtCredits(credits)} <span>credits</span>
        </p>
        <p>Your approved frame becomes a {scene.durationS}-second clip. Each clip is made once with the model you pick, and the one you approve goes in the ad.</p>
        {lip && (lip.takes + lip.pass > 0 || lip.mode === "audio") && (
          <p className="muted">Lip sync: {lip.mode === "audio" ? "this model takes the characters' voices" : "a lip-sync pass runs after the clip"}{lip.takes ? `, the scene's lines are read first (${lip.takes})` : ""}{lip.pass ? `, the pass is ${lip.pass}` : ""}. It's in the price.</p>
        )}
        {lip?.warn && <Notice tone="warning" text={lip.warn} />}
        {models.length ? <ModelOptions step="clip" models={models} value={model} durationS={scene.durationS} onPick={(x) => setModel(x.id)} {...(lip ? { lipSyncIds: lip.suggest.map((s) => s.id) } : {})} /> : <Spinner label="Loading the models" />}
        <label className="field">
          <span className="field-label">How should it move? Optional.</span>
          <input value={motion} onChange={(e) => setMotion(e.target.value)} placeholder="Slow push in, steam rising" />
        </label>
        <div className="confirm-actions">
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            Not yet
          </button>
          <button data-action="scene.animate" type="submit" className="btn btn-primary" disabled={going || !model} data-autofocus>
            {going ? "Starting" : `Animate for ${credits ?? "…"}`}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

/** A speaker id from the script ("c1", "narrator") as the character's name. */
function castName(p: Project, id: string) {
  if (id === "narrator") return "Narrator";
  const cast = videoBrief(p).characters ?? p.script?.characters ?? [];
  return cast.find((c) => c.id === id)?.name ?? id;
}

/**
 * 2026-11-03 (Keaton: "stretch the video to fit the audio or the audio to fit the video"): how the scene's clip and voice
 * meet. Trim (the default): the voice plays as read and sets the length; a long clip is cut where the line ends, a
 * short one slows by up to 1.5x, then holds. Free; from the next render. An ad's voiceover is one read, so it only
 * offers the clip side.
 */
function ClipFitRow({ p, sceneId, index, busy }: { p: Project; sceneId: string; index: number; busy: boolean }) {
  const fit = p.shots[sceneId]?.fit ?? "";
  const [saving, setSaving] = useState(false);
  const ad = isAdProject(p);
  return (
    <div className="clip-fit">
      <label className="clip-fit-label" htmlFor={`clip-fit-${sceneId}`}>
        Timing
      </label>
      <select
        id={`clip-fit-${sceneId}`}
        data-action="scene.fit"
        className={`clip-fit-pick${fit ? " is-on" : ""}`}
        value={fit}
        disabled={busy || saving}
        aria-label={`Scene ${index + 1}'s clip and voice timing`}
        title="Free; from the next render"
        onChange={async (e) => {
          const v = e.target.value;
          setSaving(true);
          await act.setSceneFit(p.id, sceneId, v === "video" || v === "audio" ? v : null);
          setSaving(false);
        }}
      >
        <option value="">Trim the clip to the voice</option>
        <option value="video">Stretch the clip to the voice</option>
        {(!ad || fit === "audio") && <option value="audio">Stretch the voice to the clip</option>}
      </select>
    </div>
  );
}

/** 2026-11-02 (Keaton's report: a reference picture for the clip step): the picture the next clip ends on (a reveal). */
function ClipEndRow({ p, sceneId, index, busy }: { p: Project; sceneId: string; index: number; busy: boolean }) {
  const end = p.shots[sceneId]?.clipEnd;
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async (pic: { id: string; name: string } | null) => {
    setSaving(true);
    await act.setClipEnd(p.id, sceneId, pic);
    setSaving(false);
    setPicking(false);
  };
  return (
    <div className="clip-end">
      {end ? (
        <>
          {(end.thumbUrl || end.url) && <img className="clip-end-thumb" src={end.thumbUrl || end.url || ""} alt="" loading="lazy" decoding="async" />}
          <span className="clip-end-text">
            Ends on <strong>{end.name}</strong>
          </span>
          <button type="button" className="btn btn-quiet btn-small" data-action="scene.clip-end" disabled={busy || saving} onClick={() => void save(null)}>
            Remove
          </button>
        </>
      ) : (
        <button type="button" className="btn btn-quiet btn-small" data-action="scene.clip-end" disabled={busy || saving} title="The next clip starts from the frame and ends on a picture you pick (a reveal). Free until you make the clip." onClick={() => setPicking(true)}>
          End on a picture
        </button>
      )}
      {picking && (
        <LibraryPick
          p={p}
          title={`Scene ${index + 1}'s clip ends on`}
          exclude={[]}
          busy={saving}
          onBack={() => setPicking(false)}
          onClose={() => setPicking(false)}
          onPick={(a) => void save({ id: a.id, name: a.name })}
        />
      )}
    </div>
  );
}
